/**
 * The staff dashboard shell, as a placeholder — the first frame of every staff
 * route while the Dashboard chunk, auth and permissions resolve.
 *
 * Drawn in the real shell's shape (`MainDashboard`): the 82px sidebar rail on
 * its own surface, the 64px top bar, and the content pane at the shell's
 * gutter, so the real shell lands on top of it without anything moving. The
 * content defaults to a generic page; pass a page-shaped skeleton as
 * `children` when the route is known.
 *
 * Cheap on purpose: no icons, no data, no module imports.
 */
import type { ReactNode } from "react";

import { Skeleton } from "@/components/ui/skeleton";
import { SHELL_CONTENT_CLASS } from "@/components/layout/shellContent";

import { PageContentSkeleton } from "./ContentSkeletons";

export function DashboardShellSkeleton({ children }: Readonly<{ children?: ReactNode }>) {
  return (
    <div
      className="fixed inset-0 flex flex-col overflow-hidden bg-background"
      aria-busy="true"
      aria-label="Loading dashboard"
    >
      <div className="flex min-h-0 flex-1 overflow-hidden">
        <aside className="hidden h-full w-[82px] shrink-0 flex-col items-start gap-[3px] border-r border-sidebar-border bg-sidebar pl-[17px] md:flex">
          <Skeleton className="mb-1.5 mt-3.5 h-[34px] w-[34px] rounded-lg" />
          {Array.from({ length: 9 }, (_, index) => (
            <span key={index} className="flex h-[46px] w-12 items-center justify-center">
              <Skeleton className="h-5 w-5 rounded-md" />
            </span>
          ))}
        </aside>

        <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
          <div className="flex h-16 shrink-0 items-center gap-3 border-b border-border bg-navbar px-4 sm:px-6 lg:px-6">
            <Skeleton className="h-9 w-32" />
            <span className="flex-1" />
            <Skeleton className="hidden h-8 w-28 sm:block" />
            <Skeleton className="h-9 w-9 rounded-full" />
          </div>

          <main className="min-h-0 flex-1 overflow-hidden">
            <div className={SHELL_CONTENT_CLASS}>{children ?? <PageContentSkeleton />}</div>
          </main>
        </div>
      </div>
    </div>
  );
}
